import { useState } from 'react'
import ECommerceHeader from '@/components/layout/ECommerceHeader'
import Footer from '@/components/layout/Footer'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useOrderStore } from '@/stores/order'
import { Package, Search, CheckCircle2, Clock, Truck, Home, AlertCircle } from 'lucide-react'

export default function TrackOrderPage() {
  const [orderNumber, setOrderNumber] = useState('')
  const [email, setEmail] = useState('')
  const [searchAttempted, setSearchAttempted] = useState(false)
  const [foundOrder, setFoundOrder] = useState<any>(null)
  const orders = useOrderStore((state) => state.orders)

  const handleSearch = () => {
    setSearchAttempted(true)
    const order = orders.find(
      (o) => o.order_number === orderNumber.toUpperCase()
    )
    setFoundOrder(order || null)
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Clock className="h-6 w-6 text-yellow-500" />
      case 'confirmed':
        return <CheckCircle2 className="h-6 w-6 text-blue-500" />
      case 'processing':
        return <Package className="h-6 w-6 text-purple-500" />
      case 'shipped':
        return <Truck className="h-6 w-6 text-orange-500" />
      case 'delivered':
        return <Home className="h-6 w-6 text-green-500" />
      case 'cancelled':
        return <AlertCircle className="h-6 w-6 text-red-500" />
      default:
        return <Package className="h-6 w-6 text-muted-foreground" />
    }
  }

  const getStatusLabel = (status: string) => {
    return status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')
  }

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  return (
    <div className="min-h-screen bg-background">
      <ECommerceHeader />

      <main className="container mx-auto px-4 py-12">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-8">
            <div className="inline-flex p-3 bg-primary/10 rounded-full mb-4">
              <Package className="h-8 w-8 text-primary" />
            </div>
            <h1 className="text-3xl font-bold text-foreground mb-2">Track Your Order</h1>
            <p className="text-muted-foreground">
              Enter your order number to track your delivery status
            </p>
          </div>

          {/* Search Form */}
          <div className="bg-card rounded-lg border border-border p-6 mb-8">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Order Number <span className="text-destructive">*</span>
                </label>
                <Input
                  placeholder="e.g., ORD-12345"
                  value={orderNumber}
                  onChange={(e) => setOrderNumber(e.target.value.toUpperCase())}
                  className="text-lg"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  You can find this in your order confirmation email
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Email Address <span className="text-destructive">*</span>
                </label>
                <Input
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  The email you used during checkout
                </p>
              </div>

              <Button
                onClick={handleSearch}
                className="w-full"
                size="lg"
                disabled={!orderNumber || !email}
              >
                <Search className="h-5 w-5 mr-2" />
                Track Order
              </Button>
            </div>
          </div>

          {/* Search Results */}
          {searchAttempted && !foundOrder && (
            <div className="bg-card rounded-lg border border-border p-8 text-center">
              <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">
                Order Not Found
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                We couldn't find an order with that order number. Please check and try again.
              </p>
              <p className="text-xs text-muted-foreground">
                Make sure you're using the correct order number from your confirmation email.
              </p>
            </div>
          )}

          {foundOrder && (
            <div className="space-y-6">
              {/* Order Summary */}
              <div className="bg-card rounded-lg border border-border p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-foreground">
                      Order #{foundOrder.order_number}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Placed on {formatDate(foundOrder.created_at)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {getStatusIcon(foundOrder.status)}
                    <span className="font-medium text-foreground">
                      {getStatusLabel(foundOrder.status)}
                    </span>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4 pt-4 border-t border-border">
                  <div>
                    <h4 className="text-sm font-medium text-foreground mb-1">
                      Delivery Address
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      {foundOrder.delivery_address.full_name}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {foundOrder.delivery_address.address_line1}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {foundOrder.delivery_address.area}, {foundOrder.delivery_address.city}
                    </p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-foreground mb-1">
                      Order Total
                    </h4>
                    <p className="text-2xl font-bold text-foreground">
                      ৳{foundOrder.total.toLocaleString()}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {foundOrder.items.length} item{foundOrder.items.length !== 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
              </div>

              {/* Order Timeline */}
              <div className="bg-card rounded-lg border border-border p-6">
                <h3 className="text-lg font-semibold text-foreground mb-4">Order Timeline</h3>

                {foundOrder.timeline && foundOrder.timeline.length > 0 ? (
                  <div className="space-y-4">
                    {foundOrder.timeline.map((event: any, index: number) => (
                      <div key={index} className="flex gap-4">
                        <div className="flex flex-col items-center">
                          <div className="p-2 bg-primary/10 rounded-full">
                            {getStatusIcon(event.status)}
                          </div>
                          {index < foundOrder.timeline.length - 1 && (
                            <div className="w-0.5 h-full bg-border my-2" />
                          )}
                        </div>
                        <div className="flex-1 pb-4">
                          <h4 className="font-medium text-foreground">
                            {getStatusLabel(event.status)}
                          </h4>
                          <p className="text-sm text-muted-foreground">{event.message}</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {formatDate(event.timestamp)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="flex gap-4">
                      <div className="p-2 bg-primary/10 rounded-full">
                        <CheckCircle2 className="h-6 w-6 text-primary" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-medium text-foreground">Order Placed</h4>
                        <p className="text-sm text-muted-foreground">
                          Your order has been received
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {formatDate(foundOrder.created_at)}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Order Items */}
              <div className="bg-card rounded-lg border border-border p-6">
                <h3 className="text-lg font-semibold text-foreground mb-4">Order Items</h3>
                <div className="space-y-4">
                  {foundOrder.items.map((item: any) => (
                    <div key={item.id} className="flex gap-4 pb-4 border-b border-border last:border-0 last:pb-0">
                      <img
                        src={item.image}
                        alt={item.title}
                        className="w-16 h-16 rounded-lg object-cover border border-border"
                      />
                      <div className="flex-1">
                        <h4 className="font-medium text-foreground">{item.title}</h4>
                        <p className="text-sm text-muted-foreground">
                          Quantity: {item.quantity}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-foreground">
                          ৳{item.subtotal.toLocaleString()}
                        </p>
                        {item.original_price > item.price && (
                          <p className="text-xs text-muted-foreground line-through">
                            ৳{(item.original_price * item.quantity).toLocaleString()}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Need Help */}
              <div className="bg-muted/50 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-primary mt-0.5" />
                  <div>
                    <h3 className="font-medium text-foreground mb-1">Need help with your order?</h3>
                    <p className="text-sm text-muted-foreground mb-2">
                      Contact our customer support team for assistance
                    </p>
                    <Button variant="outline" size="sm">
                      Contact Support
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  )
}
